import React from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, Minus, Rss, Filter,
  CheckCircle2,
} from 'lucide-react';

// ═══ 주간 트렌드 (콘텐츠 발행량) ═══
function WeeklyTrend({ data }) {
  if (!data) return null;
  const { health, sports } = data;
  // 최근 7일 발행 수 계산
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000);
    const key = d.toISOString().split('T')[0];
    const label = ['일','월','화','수','목','금','토'][d.getDay()];
    const hCount = health.recent.filter(p => p.날짜 === key).length;
    const sCount = sports.recent.filter(p => (p.created || '').startsWith(key)).length;
    days.push({ label, key, health: hCount, sports: sCount, total: hCount + sCount });
  }
  const maxVal = Math.max(...days.map(d => d.total), 1);
  const thisWeekTotal = days.reduce((s, d) => s + d.total, 0);

  // 전주 합계 (비교용)
  const lastWeekTotal = (() => {
    let sum = 0;
    for (let i = 13; i >= 7; i--) {
      const d = new Date(Date.now() - i * 86400000);
      const key = d.toISOString().split('T')[0];
      sum += health.recent.filter(p => p.날짜 === key).length;
      sum += sports.recent.filter(p => (p.created || '').startsWith(key)).length;
    }
    return sum;
  })();
  const weekDiff = lastWeekTotal > 0 ? Math.round((thisWeekTotal - lastWeekTotal) / lastWeekTotal * 100) : null;

  return (
    <div className="n-card" style={{ marginBottom: 16 }}>
      <div className="n-section-title">
        <TrendingUp size={16} /><span>주간 콘텐츠 발행</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontFamily: 'Outfit' }}>
          이번 주 <b style={{ color: 'var(--txt)' }}>{thisWeekTotal}건</b>
          {weekDiff !== null && (
            <span style={{ marginLeft: 6, color: weekDiff >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
              {weekDiff >= 0 ? '▲' : '▼'} 전주比 {Math.abs(weekDiff)}%
            </span>
          )}
        </span>
      </div>
      <div className="n-weekly-chart">
        {days.map((d, i) => (
          <div key={i} className="n-weekly-bar-wrap">
            <div className="n-weekly-bar-container">
              {d.health > 0 && <div className="n-weekly-bar health" style={{ height: `${(d.health/maxVal)*100}%` }} />}
              {d.sports > 0 && <div className="n-weekly-bar sports" style={{ height: `${(d.sports/maxVal)*100}%` }} />}
              {d.total === 0 && <div className="n-weekly-bar empty" />}
            </div>
            <span className="n-weekly-label">{d.label}</span>
            {d.total > 0 && <span className="n-weekly-count">{d.total}</span>}
          </div>
        ))}
      </div>
      <div className="n-weekly-legend">
        <span><span className="n-legend-dot" style={{ background: 'var(--type-health)' }} /> 건강</span>
        <span><span className="n-legend-dot" style={{ background: 'var(--type-sports)' }} /> 스포츠</span>
      </div>
    </div>
  );
}

// ═══ KPI 카드 ═══
function KPICards({ data }) {
  if (!data) return null;
  const { todos } = data;
  const completionRate = todos?.total > 0 ? Math.round(((todos.total - todos.pending) / todos.total) * 100) : 0;

  const THIS_MONTH_KPI = new Date().toISOString().slice(0, 7);

  // 스마트스토어 (블오크루 아카데미)
  const ssThisMonth = (() => {
    try {
      const orders = JSON.parse(localStorage.getItem('caio_smartstore_orders') || '[]');
      return orders.filter(o => (o.date || '').slice(0, 7) === THIS_MONTH_KPI);
    } catch { return []; }
  })();
  const ssRevenue = ssThisMonth.reduce((s, o) => s + (o.settle || 0), 0);

  // 애드포스트
  const adpostRevenue = (() => {
    try {
      const all = JSON.parse(localStorage.getItem('caio_adpost') || '{}');
      return (all[THIS_MONTH_KPI] || {}).actual || 0;
    } catch { return 0; }
  })();

  const totalRevenue = ssRevenue + adpostRevenue;
  const fmtMini = (n) => n >= 10000 ? `${(n / 10000).toFixed(n % 10000 === 0 ? 0 : 1)}만` : n > 0 ? `${n.toLocaleString()}원` : '—';

  const kpis = [
    {
      label: '이번달 총 매출', value: fmtMini(totalRevenue),
      change: totalRevenue > 0 ? `블오크루 ${fmtMini(ssRevenue)} · 애드 ${fmtMini(adpostRevenue)}` : '데이터 없음',
      trend: totalRevenue > 0 ? 'up' : 'neutral', sub: '블오크루 + 애드포스트', icon: BarChart3,
    },
    {
      label: '블오크루', value: fmtMini(ssRevenue),
      change: ssRevenue > 0 ? `${ssThisMonth.length}건 정산` : '엑셀 업로드 필요',
      trend: ssRevenue > 0 ? 'up' : 'neutral', sub: '스마트스토어 정산', icon: BarChart3,
    },
    {
      label: '애드포스트', value: fmtMini(adpostRevenue),
      change: adpostRevenue > 0 ? '이번달 광고 수익' : '매출탭에서 입력',
      trend: adpostRevenue > 0 ? 'up' : 'neutral', sub: '블로그 광고 수익', icon: Rss,
    },
    {
      label: '할일 완료율', value: `${completionRate}%`,
      change: `${todos.total - todos.pending}/${todos.total}건 완료`,
      trend: completionRate > 70 ? 'up' : 'down', sub: '전체 기준', icon: CheckCircle2,
    },
  ];

  const TREND_ICON = { up: TrendingUp, down: TrendingDown, neutral: Minus };

  return (
    <div className="n-kpi-grid">
      {kpis.map((k, i) => {
        const Icon = k.icon;
        const TrendIcon = TREND_ICON[k.trend];
        return (
          <div key={i} className="n-kpi-card">
            <div className="n-kpi-header">
              <Icon size={13} className="n-kpi-icon" />
              <div className="n-kpi-label">{k.label}</div>
            </div>
            <div className="n-kpi-value">{k.value}</div>
            <div className={`n-kpi-change ${k.trend}`}>
              <TrendIcon size={11} />
              {k.change}
            </div>
            <div className="n-kpi-sub">{k.sub}</div>
          </div>
        );
      })}
    </div>
  );
}

// ═══ 매출 달성 도넛 ═══
function RevenueDonut() {
  const THIS_MONTH = new Date().toISOString().slice(0, 7);

  const ssRevenue = (() => {
    try {
      const orders = JSON.parse(localStorage.getItem('caio_smartstore_orders') || '[]');
      return orders.filter(o => (o.date || '').slice(0, 7) === THIS_MONTH).reduce((s, o) => s + (o.settle || 0), 0);
    } catch { return 0; }
  })();
  const adpostRevenue = (() => {
    try {
      const all = JSON.parse(localStorage.getItem('caio_adpost') || '{}');
      return (all[THIS_MONTH] || {}).actual || 0;
    } catch { return 0; }
  })();
  const totalRevenue = ssRevenue + adpostRevenue;

  const targetOverride = parseInt(localStorage.getItem('caio_monthly_target_override') || '', 10);
  const COURSE_TARGETS_BASE = 100000*10 + 150000*20 + 200000*10 + 397000*5 + 779000*1 + 530000*1;
  const adpostTarget = (() => {
    try { const all = JSON.parse(localStorage.getItem('caio_adpost') || '{}'); return (all[THIS_MONTH] || {}).target || 500000; } catch { return 500000; }
  })();
  const totalTarget = isNaN(targetOverride) ? COURSE_TARGETS_BASE + adpostTarget : targetOverride;

  const achieved = Math.min(totalRevenue, totalTarget);
  const remaining = Math.max(0, totalTarget - totalRevenue);
  const pct = totalTarget > 0 ? Math.min(100, Math.round((totalRevenue / totalTarget) * 100)) : 0;

  const fmtW = (n) => n >= 10000 ? `${(n / 10000).toFixed(n % 10000 === 0 ? 0 : 1)}만` : `${n.toLocaleString()}`;

  const segments = [
    { label: '달성', value: achieved, color: 'var(--gold)' },
    { label: '목표 잔여', value: remaining || (totalTarget * 0.01), color: 'var(--bg3)' },
  ];
  const total = segments.reduce((s, sg) => s + sg.value, 0);
  const r = 52, cx = 70, cy = 70;
  let cumulative = 0;

  return (
    <div className="n-chart-card">
      <div className="n-chart-header"><BarChart3 size={14} /><span>매출 달성</span></div>
      <div className="n-donut-wrap">
        <svg viewBox="0 0 140 140" className="n-donut-svg" role="img" aria-labelledby="donut-title-revenue">
          <title id="donut-title-revenue">매출 달성 — {pct}%</title>
          {segments.map((seg, i) => {
            const pctSeg = seg.value / total;
            const circ = 2 * Math.PI * r;
            const dash = pctSeg * circ, gap = circ - dash;
            const offset = -cumulative * circ - circ * 0.25;
            cumulative += pctSeg;
            return <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={seg.color} strokeWidth={18} strokeDasharray={`${dash} ${gap}`} strokeDashoffset={offset} />;
          })}
          <text x={cx} y={cy - 5} textAnchor="middle" fill="var(--gold)" fontSize="19" fontWeight="700">{pct}%</text>
          <text x={cx} y={cy + 10} textAnchor="middle" fill="var(--txt3)" fontSize="10">달성률</text>
        </svg>
        <div className="n-donut-legend">
          <div className="n-legend-row">
            <span className="n-legend-dot" style={{ background: 'var(--gold)' }} />
            <span className="n-legend-name">달성</span>
            <span className="n-legend-val" style={{ color: 'var(--gold)' }}>{fmtW(achieved)}원</span>
          </div>
          <div className="n-legend-row">
            <span className="n-legend-dot" style={{ background: 'var(--bg4)' }} />
            <span className="n-legend-name">잔여</span>
            <span className="n-legend-val" style={{ color: 'var(--txt3)' }}>{fmtW(remaining)}원</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 10, color: 'var(--txt3)', borderTop: '1px solid var(--brd)', paddingTop: 8 }}>
            목표 {fmtW(totalTarget)}원
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══ 아카데미 퍼널 시각화 ═══
function AcademyFunnel() {
  const funnel = [
    { name: '블다챌', price: '9,900원', width: '100%', color: 'var(--product-1)' },
    { name: '기초반', price: '100,000원', width: '60%', color: 'var(--product-2)' },
    { name: '제목짓기반', price: '150,000원', width: '40%', color: 'var(--product-3)' },
    { name: '글쓰기구조반', price: '200,000원', width: '28%', color: 'var(--product-4)' },
    { name: '인플컨설팅', price: '530,000원', width: '12%', color: 'var(--product-5)' },
  ];

  return (
    <div className="n-card" style={{ marginBottom: 16 }}>
      <div className="n-section-title"><Filter size={16} /><span>아카데미 퍼널</span></div>
      <div className="n-funnel">
        {funnel.map((f, i) => (
          <div key={i} className="n-funnel-row">
            <div className="n-funnel-bar" style={{ width: f.width, background: f.color }}>
              <span className="n-funnel-name">{f.name}</span>
              <span className="n-funnel-price">{f.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══ 스파크라인 ═══
function Sparkline({ trend, color }) {
  if (!trend || trend.length < 2) return null;
  const max = Math.max(...trend.map(p => p.ratio), 1);
  const W = 240, H = 44, pad = 4;
  const pts = trend.map((p, i) => {
    const x = pad + (i / (trend.length - 1)) * (W - pad * 2);
    const y = H - pad - (p.ratio / max) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const lastPt = trend[trend.length - 1];
  const lastX = W - pad;
  const lastY = H - pad - (lastPt.ratio / max) * (H - pad * 2);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="n-kw-sparkline" preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="3" fill={color} />
    </svg>
  );
}

export { WeeklyTrend, KPICards, RevenueDonut, AcademyFunnel, Sparkline };
